import React, { useState, useRef, useEffect } from 'react';
import { HiMenu, HiOfficeBuilding, HiChevronDown } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import './Header.css';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { branches, selectedBranchId, setSelectedBranchId, isAdmin, isManager, currentBranch } = useBranch();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const branchDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(e.target)) {
        setBranchDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const effectiveBranchId = selectedBranchId ?? (branches[0]?.id ?? branches[0]?.branch_id) ?? null;
  const selectedBranch = branches.find((b) => (b.id ?? b.branch_id) === effectiveBranchId) || currentBranch;

  const displayName = user?.name || user?.username || 'User';

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-section header-left">
          <button type="button" className="header-menu-toggle" onClick={() => onMenuClick?.()} aria-label="Open menu">
            <HiMenu className="menu-icon" />
          </button>
        </div>

        <div className="header-section header-center">
          <span className="header-user">Welcome, {displayName}</span>
        </div>

        <div className="header-section header-right">
          {(isAdmin || isManager) && (branches.length > 0 || currentBranch) && (
            <div className="header-branch-wrap" ref={branchDropdownRef}>
              {isAdmin ? (
                <div className="branch-selector">
                  <button
                    type="button"
                    className="branch-selector-trigger"
                    onClick={() => setBranchDropdownOpen((o) => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={branchDropdownOpen}
                    aria-label="Select branch"
                  >
                    <HiOfficeBuilding className="branch-selector-icon" aria-hidden />
                    <span className="branch-selector-value">
                      {selectedBranch?.name ?? 'Select branch'}
                      {selectedBranch?.is_main ? ' (Main)' : ''}
                    </span>
                    <HiChevronDown className={`branch-selector-chevron ${branchDropdownOpen ? 'open' : ''}`} aria-hidden />
                  </button>
                  {branchDropdownOpen && (
                    <ul className="branch-dropdown" role="listbox" aria-label="Branch list">
                      {branches.map((b) => {
                        const bid = b.id ?? b.branch_id;
                        const isSelected = bid === effectiveBranchId;
                        return (
                          <li key={bid} role="option" aria-selected={isSelected}>
                            <button
                              type="button"
                              className={`branch-dropdown-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedBranchId(bid);
                                setBranchDropdownOpen(false);
                              }}
                            >
                              <HiOfficeBuilding className="branch-item-icon" />
                              <span>{b.name}{b.is_main ? ' (Main)' : ''}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="branch-readonly">
                  <HiOfficeBuilding className="branch-readonly-icon" />
                  <span className="branch-name-display">{currentBranch?.name ?? (user?.branch?.name || 'My Branch')}</span>
                </div>
              )}
            </div>
          )}
          <button onClick={toggleDarkMode} className="theme-toggle-btn" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
